import { formatDate as formatDateUtil, formatDateTime as formatDateTimeUtil } from '../../utils/dateUtils';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, Calendar, CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { useGetUserMembershipStatusQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import Navbar from '../../components/common/Navbar';

const MembershipStatus: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const { data: membershipStatusResponse, isLoading } = useGetUserMembershipStatusQuery(undefined, {
    skip: !user,
  });

  const membershipStatus = membershipStatusResponse?.data;
  const membership = membershipStatus?.membership;
  const hasActiveMembership = membershipStatus?.is_active || false;
  const hasMembership = membershipStatus?.has_membership || false;
  const daysRemaining = membershipStatus?.days_remaining || 0;
  const maxBookings = membership?.plan?.max_bookings_per_month || 0;

  const formatDate = (dateString: string | Date | null | undefined) => formatDateUtil(dateString);;

  const formatCurrency = (amount: number | string) => {
    const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    return `$${numAmount.toLocaleString('en-US')}`;
  };

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Membership Status</h1>
              <p className="text-gray-600">View your membership details and booking limits</p>
            </div>
            {hasActiveMembership ? (
              <Badge variant="success" className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3" />
                Active
              </Badge>
            ) : (
              <Badge variant="danger" className="flex items-center gap-1">
                <XCircle className="w-3 h-3" />
                No Active Membership
              </Badge>
            )}
          </div>

          {!hasMembership ? (
            <div className="text-center py-12">
              <Crown className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Active Membership</h3>
              <p className="text-gray-600 mb-6">
                You don't have an active membership. Purchase a membership plan to start booking apartments.
              </p>
              <Button onClick={() => navigate('/membership/plans')} className="bg-gray-800 hover:bg-gray-900">
                View Membership Plans
              </Button>
            </div>
          ) : membership ? (
            <div className="space-y-6">
              {/* Membership Details */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Membership Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Plan Name</label>
                    <p className="text-gray-900 font-semibold mt-1">{membership.plan.name}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Status</label>
                    <div className="mt-1">
                      {membership.status === 'active' ? (
                        <Badge variant="success" className="bg-green-500 text-white">
                          Active
                        </Badge>
                      ) : membership.status === 'expired' ? (
                        <Badge variant="danger" className="bg-red-500 text-white">
                          Expired
                        </Badge>
                      ) : (
                        <Badge variant="danger" className="bg-gray-500 text-white">
                          Cancelled
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Start Date</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      <p className="text-gray-900">{formatDate(membership.start_date)}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">End Date</label>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      <p className="text-gray-900">{formatDate(membership.end_date)}</p>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700">Payment Type</label>
                    <p className="text-gray-900 mt-1 capitalize">
                      {membership.payment_type === 'one_time' ? 'One-time Payment' : 'Monthly Installment'}
                    </p>
                  </div>
                  {membership.payment_type === 'monthly_installment' && (
                    <>
                      <div>
                        <label className="text-sm font-medium text-gray-700">Installments</label>
                        <p className="text-gray-900 mt-1">
                          {membership.installments_paid || 0} / {membership.total_installments || 0} paid
                        </p>
                      </div>
                      {membership.next_installment_date && (
                        <div>
                          <label className="text-sm font-medium text-gray-700">Next Payment</label>
                          <p className="text-gray-900 mt-1">{formatDate(membership.next_installment_date)}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Membership Info */}
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Membership Information</h2>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Days Remaining</span>
                    <span className="font-semibold text-gray-900">
                      {daysRemaining} days
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-700">Max Bookings Per Month</span>
                    <span className="font-semibold text-gray-900">
                      {maxBookings}
                    </span>
                  </div>
                  {!hasActiveMembership && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-3">
                      <div className="flex items-start gap-2">
                        <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <p className="text-sm text-yellow-700">
                          Your membership has expired. Please renew to continue booking.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/apartments')}
                  disabled={!hasActiveMembership}
                  className="flex-1 bg-gray-800 hover:bg-gray-900"
                >
                  Browse Apartments
                </Button>
                <Button
                  onClick={() => navigate('/bookings')}
                  variant="outline"
                  className="flex-1"
                >
                  View My Bookings
                </Button>
              </div>
            </div>
          ) : null}
        </Card>
      </div>
      </div>
    </>
  );
};

export default MembershipStatus;
